import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useAsync } from '../hooks/useAsync';
import { eur, usd } from '../services/format';
import { BarChart } from '../components/charts/BarChart';
import { Alert, PageTitle, Stat } from '../components/ui';

export function Dashboard() {
  const { t, i18n } = useTranslation();
  const { data, error, loading } = useAsync(() => api.stats(), []);
  const weekLabel = (p: string) => {
    const d = new Date(`${p}T00:00:00`);
    return Number.isNaN(d.getTime()) ? p : new Intl.DateTimeFormat(i18n.language === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'short' }).format(d);
  };
  return (
    <div>
      <PageTitle title={t('dashboard.title')} subtitle={t('dashboard.subtitle')} />
      {error && <Alert>{error}</Alert>}
      {loading && !data && <p className="text-faint">{t('common.loading')}</p>}
      {data && (
        <div className="flex flex-col gap-5">
          <section>
            <h2 className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-faint">{t('nav.users')}</h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat label={t('dashboard.usersTotal')} value={data.users_total} />
              <Stat label={t('dashboard.usersVerified')} value={data.users_verified} hint={t('dashboard.of', { total: data.users_total })} />
              <Stat label={t('dashboard.usersActive')} value={data.users_active} />
              <Stat label={t('dashboard.usersStaff')} value={data.users_staff} />
            </div>
          </section>
          <section>
            <h2 className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-faint">{t('nav.organizations')}</h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              <Stat label={t('dashboard.orgsTotal')} value={data.organizations_total} />
              <Stat label={t('orgs.kind.team')} value={data.organizations_team} />
              <Stat label={t('orgs.kind.personal')} value={data.organizations_personal} />
              <Stat label={t('orgs.status.suspended')} value={data.organizations_suspended} tone={data.organizations_suspended ? 'warn' : undefined} />
              <Stat label={t('orgs.status.archived')} value={data.organizations_archived} />
            </div>
          </section>
          <section>
            <h2 className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-faint">{t('dashboard.operations')}</h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Link to="/invoices?status=sent">
                <Stat label={t('dashboard.invoicesOutstanding')} value={data.invoices_outstanding_count} hint={usd(data.invoices_outstanding_amount, i18n.language)} tone={data.invoices_outstanding_count ? 'warn' : undefined} />
              </Link>
              <Link to="/invoices?status=draft">
                <Stat label={t('dashboard.invoicesDraft')} value={data.invoices_draft_count} />
              </Link>
              <Link to="/mail?status=queued">
                <Stat label={t('dashboard.outboxQueued')} value={data.outbox_queued} />
              </Link>
              <Link to="/mail?status=failed">
                <Stat label={t('dashboard.outboxFailed')} value={data.outbox_failed} tone={data.outbox_failed ? 'bad' : undefined} />
              </Link>
            </div>
          </section>
          <div className="grid gap-4 lg:grid-cols-2">
            <BarChart title={t('dashboard.signupsPerWeek')} buckets={data.signups_per_week} format={(v) => String(Math.round(v))} periodLabel={weekLabel} />
            <BarChart title={t('dashboard.spendPerMonth')} buckets={data.billed_per_month} format={(v) => eur(v, i18n.language)} color="#0B5A2F" />
            <BarChart title={t('dashboard.costPerMonth')} buckets={data.cost_per_month} format={(v) => usd(v, i18n.language)} />
            <BarChart title={t('dashboard.internalCostPerMonth')} buckets={data.internal_cost_per_month ?? []} format={(v) => usd(v, i18n.language)} />
          </div>
        </div>
      )}
    </div>
  );
}
